import { AccessLevel, PermissionName } from './permissions.types';

export interface PermissionAccessLevel {
  permission: PermissionName;
  accessLevel: AccessLevel;
}

/**
 * Access level per permission. Partial because callers hold a subset of the
 * catalog: a single role's column in the grid, or one role's form values.
 */
export type AccessLevelByPermission = Partial<
  Record<PermissionName, AccessLevel>
>;

/**
 * A permission whose allowed access levels are decided by other permissions.
 *
 * `matrix` is expected to cover every combination of the dependencies' levels;
 * a combination with no row is treated as unconstrained.
 */
interface PermissionConstraint<
  TDependsOn extends readonly PermissionName[] = readonly PermissionName[],
> {
  permission: PermissionName;
  dependsOn: TDependsOn;
  matrix: ReadonlyArray<{
    /** Dependency access levels, in the same order as `dependsOn`. */
    when: { [K in keyof TDependsOn]: AccessLevel };
    allow: readonly AccessLevel[];
  }>;
}

/**
 * Infers `dependsOn` as a tuple, so every `when` row is length checked against
 * the dependencies it describes.
 */
const defineConstraint = <const TDependsOn extends readonly PermissionName[]>(
  constraint: PermissionConstraint<TDependsOn>,
): PermissionConstraint => constraint;

/**
 * Permissions that cannot be configured independently, as full matrices of
 * dependency levels (0 - none, 1 - view, 2 - edit).
 *
 * `groups` follows employee access: editing groups requires editing employees,
 * while masked employee data only allows viewing groups.
 *
 * `automations` assign benefits to employee groups, so an automation can never
 * reach further than both `benefits` and `groups` allow.
 */
const PERMISSION_CONSTRAINTS: readonly PermissionConstraint[] = [
  defineConstraint({
    permission: 'groups',
    dependsOn: ['employees'],
    matrix: [
      { when: [0], allow: [1] },
      { when: [1], allow: [1, 2] },
      { when: [2], allow: [2] },
    ],
  }),
  defineConstraint({
    permission: 'automations',
    dependsOn: ['benefits', 'groups'],
    matrix: [
      { when: [0, 1], allow: [0] },
      { when: [0, 2], allow: [0] },
      { when: [1, 1], allow: [0, 1] },
      { when: [1, 2], allow: [0, 1] },
      { when: [2, 1], allow: [0, 1] },
      { when: [2, 2], allow: [0, 1, 2] },
    ],
  }),
];

export interface PermissionConflict {
  /** Permission whose access level its dependencies do not allow. */
  permission: PermissionName;
  accessLevel: AccessLevel;
  /** Levels that decided the allowed set, in matrix order. */
  dependencies: PermissionAccessLevel[];
  allowedAccessLevels: readonly AccessLevel[];
}

/**
 * Finds permissions set to an access level their dependencies do not allow.
 * Constraints are skipped unless every permission they involve is present, so
 * partial sets can be passed safely.
 */
export const getPermissionConflicts = (
  accessLevelByPermission: AccessLevelByPermission,
): PermissionConflict[] => {
  const conflicts: PermissionConflict[] = [];

  for (const { permission, dependsOn, matrix } of PERMISSION_CONSTRAINTS) {
    const accessLevel = accessLevelByPermission[permission];

    if (accessLevel === undefined) continue;

    const dependencies: PermissionAccessLevel[] = [];

    for (const dependency of dependsOn) {
      const dependencyAccessLevel = accessLevelByPermission[dependency];

      if (dependencyAccessLevel === undefined) break;

      dependencies.push({
        permission: dependency,
        accessLevel: dependencyAccessLevel,
      });
    }

    if (dependencies.length !== dependsOn.length) continue;

    const row = matrix.find(({ when }) =>
      when.every((level, i) => level === dependencies[i].accessLevel),
    );

    if (!row || row.allow.includes(accessLevel)) continue;

    conflicts.push({
      permission,
      accessLevel,
      dependencies,
      allowedAccessLevels: row.allow,
    });
  }

  return conflicts;
};

/** Groups a flat permission list into the shape the validation expects. */
export const toAccessLevelByPermission = (
  permissions: PermissionAccessLevel[],
): AccessLevelByPermission => {
  const accessLevelByPermission: AccessLevelByPermission = {};

  for (const { permission, accessLevel } of permissions) {
    accessLevelByPermission[permission] = accessLevel;
  }

  return accessLevelByPermission;
};
